import {
  LOOT_QTY_MIN,
  LOOT_QTY_MAX,
  LOOT_QUALITY_MIN,
  LOOT_QUALITY_MAX,
  LOOT_QUALITY_DEFAULT,
  parseItemQualityInput,
  clampLootQuantity,
  clampLootQuality,
  unitKindFromLootEntry,
  minLootQuantity,
  isFullLootQuantityMove,
  formatLootQuantityDisplay,
} from "../../../../../../../utils/lootChipMeta";

export {
  LOOT_QTY_MIN,
  LOOT_QTY_MAX,
  LOOT_QUALITY_MIN,
  LOOT_QUALITY_MAX,
  LOOT_QUALITY_DEFAULT,
  parseItemQualityInput,
  clampLootQuantity,
  clampLootQuality,
  unitKindFromLootEntry,
  minLootQuantity,
  isFullLootQuantityMove,
  formatLootQuantityDisplay,
};

export function clampPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

/** Per-participant raw pool split (before per-line payout toggles). */
export function computeParticipantRawByKey(totalTrunc, percentClamped, participantList) {
  const rawByKey = {};
  const N = participantList.length;
  if (totalTrunc == null || !Number.isFinite(totalTrunc) || N <= 0) return rawByKey;
  const T = Math.max(0, Math.trunc(totalTrunc));
  const orgNominal = Math.floor((T * percentClamped) / 100);
  const pool0 = Math.max(0, T - orgNominal);
  const shareCeil = Math.ceil(pool0 / N);
  const totalCeil = shareCeil * N;
  const excess = totalCeil - pool0;
  const orgAdj = orgNominal - excess;
  if (orgAdj >= 0) {
    for (const p of participantList) rawByKey[p.key] = shareCeil;
  } else {
    const base = Math.floor(pool0 / N);
    const remainder = pool0 - base * N;
    participantList.forEach((p, idx) => {
      rawByKey[p.key] = base + (idx < remainder ? 1 : 0);
    });
  }
  return rawByKey;
}

/** Mission contract: equal split across all participants, no org share. */
export function computeEqualMissionContractShareByKey(totalTrunc, participantList) {
  const rawByKey = {};
  const N = participantList.length;
  if (totalTrunc == null || !Number.isFinite(totalTrunc) || N <= 0) return rawByKey;
  const T = Math.max(0, Math.trunc(totalTrunc));
  const base = Math.floor(T / N);
  const remainder = T - base * N;
  participantList.forEach((p, idx) => {
    rawByKey[p.key] = base + (idx < remainder ? 1 : 0);
  });
  return rawByKey;
}

export function parseUecInput(raw) {
  const s = String(raw ?? "");
  const digitsOnly = s.replace(/[^\d]/g, "");
  if (!digitsOnly) return null;
  const n = Number(digitsOnly);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.trunc(n));
}

/** Integer UEC display: ASCII space as thousands separator (no decimals). */
export function formatUec(n) {
  if (n == null) return "";
  const num = Number(n);
  if (!Number.isFinite(num)) return "";
  const t = String(Math.trunc(num));
  if (t === "0") return "0";
  const neg = t.startsWith("-");
  const digits = neg ? t.slice(1) : t;
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return neg ? `-${grouped}` : grouped;
}

/** Count digit characters strictly before `index` in the visible string (spaces ignored). */
export function countDigitsLeftOfIndex(s, index) {
  const n = Math.max(0, Math.min(Number(index) || 0, s.length));
  let c = 0;
  for (let i = 0; i < n; i += 1) {
    if (/\d/.test(s[i])) c += 1;
  }
  return c;
}

/** Caret offset in a formatted UEC string so that `digitIndex` digits lie to the left of the caret. */
export function caretOffsetAfterDigitIndex(formatted, digitIndex) {
  if (digitIndex <= 0) return 0;
  let d = 0;
  for (let i = 0; i < formatted.length; i += 1) {
    if (/\d/.test(formatted[i])) {
      d += 1;
      if (d === digitIndex) return i + 1;
    }
  }
  return formatted.length;
}

/** Max rows returned by `/api/sc-items/search` and shown in loot typeahead dropdowns. */
export const LOOT_SUGGEST_API_LIMIT = 50;
export const LOOT_SUGGEST_LIST_LIMIT = LOOT_SUGGEST_API_LIMIT;

/** Stable catalog identity from an sc-items search row. */
export function buildLootKey(row) {
  const sid = row?.snapshotId != null ? String(row.snapshotId) : "na";
  const code = row?.itemCode != null ? String(row.itemCode) : "na";
  const tt = row?.tableType != null ? String(row.tableType) : "na";
  const gv = row?.gameVersion != null ? String(row.gameVersion) : "na";
  return `${tt}::${gv}::${sid}::${code}`;
}

/** Unique key for one loot line in a benefits draft (same catalog item may appear many times). */
export function buildLootInstanceKey(row) {
  const catalogKey = buildLootKey(row);
  const suffix =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`;
  return `${catalogKey}::inst::${suffix}`;
}

export function isMentionOnlyLootRow(row) {
  const tt = row?.tableType != null ? String(row.tableType) : "";
  return tt === "missionGiver" || tt === "namedCharacters" || tt === "creatures";
}

export function formatLootPrimaryLabel(row) {
  if (!row) return "";
  const base =
    typeof row.label === "string" && row.label.trim() !== "" ? row.label.trim() : String(row.itemCode || "Item");
  if (isMentionOnlyLootRow(row)) return base;
  const code = row.itemCode != null ? String(row.itemCode) : "";
  const c = code.toLowerCase();
  if (c.startsWith("harvestable_")) return `${base} (unit)`;
  if (
    c.includes("_fl_1scu_") ||
    c.includes("_scu_") ||
    c.includes("commodity_") ||
    c.startsWith("carryable_")
  ) {
    return `${base} (SCU)`;
  }
  return base;
}

export function formatLootMeta(row) {
  if (!row) return "";
  const bits = [row.tableType, row.gameVersion].filter(Boolean).map(String);
  return bits.join(" · ");
}

export function lootUnitKindDefault(row) {
  const code = row?.itemCode != null ? String(row.itemCode).toLowerCase() : "";
  if (code.startsWith("harvestable_")) return "items";
  if (
    code.includes("_fl_1scu_") ||
    code.includes("_scu_") ||
    code.includes("commodity_") ||
    code.startsWith("carryable_")
  ) {
    return "SCU";
  }
  return "items";
}

export function lootBaseDisplayLabel(lootOrRow) {
  const loot = lootOrRow?.row != null ? lootOrRow : { row: lootOrRow, label: lootOrRow?.label };
  const raw =
    (typeof loot?.row?.label === "string" && loot.row.label.trim()) ||
    (typeof loot?.label === "string" && loot.label.trim()) ||
    (loot?.row?.itemCode != null ? String(loot.row.itemCode) : "") ||
    (loot?.itemCode != null ? String(loot.itemCode) : "");
  return String(raw).replace(/\s*\((unit|SCU)\)\s*$/i, "").trim() || "Item";
}

export function lootQuantityFromEntry(loot) {
  const row = loot?.row && typeof loot.row === "object" ? loot.row : null;
  const raw =
    loot?.quantity ??
    loot?.unitCount ??
    row?.quantity ??
    row?.unitCount ??
    row?.count ??
    1;
  return clampLootQuantity(raw, unitKindFromLootEntry(loot));
}

export function lootEntrySupportsQuality(loot) {
  if (!loot || typeof loot !== "object") return false;
  if (loot?.meta?.mentionOnly === true) return false;
  const row = loot?.row && typeof loot.row === "object" ? loot.row : loot;
  // Prefer live row classification so whitelist expansions upgrade existing drafts
  // that still carry a stale meta.supportsQuality === false.
  if (lootHasQuality(row)) return true;
  if (loot?.meta?.supportsQuality === true) return true;
  return false;
}

export function lootQualityFromEntry(loot) {
  if (!lootEntrySupportsQuality(loot)) return null;
  const raw =
    loot?.itemQuality ??
    loot?.quality ??
    loot?.meta?.qualityDefault ??
    loot?.meta?.quality ??
    loot?.row?.itemQuality ??
    loot?.row?.quality ??
    LOOT_QUALITY_DEFAULT;
  return clampLootQuality(raw);
}

export function lootUnitKindLabel(unitKindDefault, quantity) {
  const uk = unitKindDefault != null ? String(unitKindDefault) : "items";
  if (uk === "SCU") return "SCU";
  return clampLootQuantity(quantity, uk) === 1 ? "item" : "items";
}

export function patchLootEntryQuantityQuality(entry, { quantity, quality } = {}) {
  if (!entry || typeof entry !== "object") return entry;
  const next = { ...entry };
  const unitKind = unitKindFromLootEntry(entry);
  if (quantity != null) next.quantity = clampLootQuantity(quantity, unitKind);
  if (quality != null && lootEntrySupportsQuality(entry)) {
    const q = clampLootQuality(quality);
    next.quality = q;
    next.itemQuality = q;
    if (next.meta && typeof next.meta === "object") {
      next.meta = { ...next.meta, supportsQuality: true, qualityDefault: q };
    } else {
      next.meta = { supportsQuality: true, qualityDefault: q };
    }
  }
  return next;
}

/**
 * Catalog tableTypes that always carry a quality tier (default 500).
 * Keep in sync with backend QUALITY_ITEM_TABLE_TYPES (inventoryItemQuality.js).
 */
export const QUALITY_ITEM_TABLE_TYPES = new Set([
  "FPSWeapons",
  "WeaponModifier",
  "Armor_Helmet",
  "Armor_Torso",
  "Armor_Arms",
  "Armor_Legs",
  "Armor_Backpacks",
  "Char_Armor_Undersuit",
  "Clothing_Hats",
  "Clothing_Torso0",
  "Clothing_Torso1",
  "Clothing_Torso2",
  "Clothing_Legs",
  "Clothing_Feet",
  "Clothing_Hands",
  "Clothing_Backpack",
  "Clothing_Glasses",
  "PowerPlant",
  "Shield",
  "Cooler",
  "QuantumDrive",
  "JumpDrive",
  "LifeSupport",
  "GravityGenerator",
  "Blade",
  "Radar",
  "Scanner",
  "Relay",
  "Fuses",
  "Paints",
  "ShipWeapons",
  "EMP",
  "Missiles",
  "ExplosiveBombs",
  "TractorBeam",
  "MiningLaser",
  "RocketPods",
  "SalvageHead",
  "SalvageModifiers",
]);

export function lootHasQuality(row) {
  if (!row || typeof row !== "object") return false;
  const code = row.itemCode != null ? String(row.itemCode).toLowerCase() : "";
  const payload = row.payload && typeof row.payload === "object" ? row.payload : null;
  const tt = row.tableType != null ? String(row.tableType) : "";
  if (/^blueprints/i.test(tt)) return false;
  if (code.includes("blueprint")) return false;
  if (QUALITY_ITEM_TABLE_TYPES.has(tt)) return true;
  const isLootTable = tt === "variousItems" || tt === "Carryables" || tt === "entityHarvestables";
  if (!isLootTable) return false;
  const hasResourceType = Array.isArray(payload?.resourceTypeRecordNames) && payload.resourceTypeRecordNames.length > 0;
  const hasMaterialKey = typeof payload?.materialFamilyKey === "string" && payload.materialFamilyKey.trim() !== "";
  const hasCraftingKey =
    Array.isArray(payload?.craftingIngredientNameKeys) && payload.craftingIngredientNameKeys.length > 0;
  const looksMineral =
    code.includes("_commodity_mineral_") || code.startsWith("harvestable_mineral_") || code.includes("mineral_");
  const looksMetal = code.includes("_commodity_metal_") || code.includes("commodity_metal");
  return Boolean(
    hasResourceType ||
      (looksMineral && (hasMaterialKey || hasCraftingKey)) ||
      (looksMetal && (hasMaterialKey || hasCraftingKey))
  );
}

function itemCodeLooksLikePackedCommodityMineralOrMetalBox(itemCode) {
  const c = itemCode != null ? String(itemCode).toLowerCase() : "";
  if (!c) return false;
  return (
    c.includes("commodity_mineral_") ||
    c.includes("commodity_minerals_") ||
    c.includes("commodity_metal_")
  );
}

/** Fallback when `payload.materialFamilyKey` is missing — must match backend heuristics. */
function commodityPackedDedupKeyFromItemCode(itemCode) {
  const c = itemCode != null ? String(itemCode).toLowerCase() : "";
  if (!c) return "";
  const minI = c.lastIndexOf("commodity_mineral_");
  if (minI >= 0) {
    const rest = c.slice(minI + "commodity_mineral_".length);
    const parts = rest.split("_").filter(Boolean);
    if (parts[0] === "raw" && parts[1]) {
      return `mineral:${parts[1]}_raw`;
    }
    const seg = parts[0];
    return seg ? `mineral:${seg}` : "";
  }
  const minPlI = c.lastIndexOf("commodity_minerals_");
  if (minPlI >= 0) {
    const rest = c.slice(minPlI + "commodity_minerals_".length);
    const seg = rest.split("_").filter(Boolean)[0];
    return seg ? `mineral:${seg}` : "";
  }
  const metI = c.lastIndexOf("commodity_metal_");
  if (metI >= 0) {
    const rest = c.slice(metI + "commodity_metal_".length);
    const seg = rest.split("_").filter(Boolean)[0];
    return seg ? `metal:${seg}` : "";
  }
  return "";
}

/**
 * Canonical: `materialFamilyKey` in payload, then `commodity_*` in itemCode (incl. `commodity_minerals_` plural).
 */
function lootMaterialDedupKeyForRow(row) {
  const pl = row?.payload;
  if (pl && typeof pl === "object" && typeof pl.materialFamilyKey === "string" && pl.materialFamilyKey.trim() !== "") {
    return `mineral:${pl.materialFamilyKey.trim().toLowerCase()}`;
  }
  return commodityPackedDedupKeyFromItemCode(row?.itemCode);
}

/** If the same packed commodity exists in `variousItems`, drop the redundant `Carryables` row (same duplicate label). */
export function dedupeLootSuggestPreferVariousOverCarryables(rows) {
  if (!Array.isArray(rows) || rows.length < 2) return rows;
  const keysWithVarious = new Set();
  for (let i = 0; i < rows.length; i += 1) {
    const r = rows[i];
    if (String(r.tableType) !== "variousItems") continue;
    const k = lootMaterialDedupKeyForRow(r);
    if (k) keysWithVarious.add(k);
  }
  if (keysWithVarious.size === 0) return rows;
  return rows.filter((r) => {
    if (String(r.tableType) !== "Carryables") return true;
    const k = lootMaterialDedupKeyForRow(r);
    if (!k || !keysWithVarious.has(k)) return true;
    if (!itemCodeLooksLikePackedCommodityMineralOrMetalBox(r.itemCode)) return true;
    return false;
  });
}

/**
 * Fallback: same snapshot `label` in `variousItems` and `Carryables` (some exports omit the commodity
 * substring in `variousItems` itemCode) — still drop the packed commodity in `Carryables`.
 */
function isBlueprintLootSuggestRow(row) {
  const tt = row?.tableType != null ? String(row.tableType) : "";
  if (/^blueprints/i.test(tt)) return true;
  const code = row?.itemCode != null ? String(row.itemCode).toLowerCase() : "";
  return code.includes("blueprint");
}

function scoreLootSuggestMatch(row, queryNorm) {
  const lbl = row?.label != null ? String(row.label).trim().toLowerCase() : "";
  if (!lbl || !queryNorm) return 99;
  if (lbl.startsWith(queryNorm)) return 0;
  if (lbl.includes(` ${queryNorm}`)) return 1;
  if (lbl.includes(`(${queryNorm}`)) return 2;
  if (lbl.includes(queryNorm)) return 3;
  return 4;
}

/** Re-rank API rows so prefix label matches beat blueprint substring hits within the dropdown limit. */
export function sortLootSuggestByRelevance(rows, searchLabel) {
  if (!Array.isArray(rows) || rows.length < 2) return rows;
  const q = String(searchLabel || "").trim().toLowerCase();
  if (!q) return rows;
  return [...rows].sort((a, b) => {
    const sa = scoreLootSuggestMatch(a, q);
    const sb = scoreLootSuggestMatch(b, q);
    if (sa !== sb) return sa - sb;
    const ba = isBlueprintLootSuggestRow(a) ? 1 : 0;
    const bb = isBlueprintLootSuggestRow(b) ? 1 : 0;
    if (ba !== bb) return ba - bb;
    const la = String(a.label || "").toLowerCase();
    const lb = String(b.label || "").toLowerCase();
    if (la < lb) return -1;
    if (la > lb) return 1;
    return String(a.itemCode || "").localeCompare(String(b.itemCode || ""));
  });
}

function compareGameVersionPartsDesc(a, b) {
  const pa = String(a || "")
    .split(".")
    .map((p) => parseInt(p, 10))
    .map((n) => (Number.isFinite(n) ? n : 0));
  const pb = String(b || "")
    .split(".")
    .map((p) => parseInt(p, 10))
    .map((n) => (Number.isFinite(n) ? n : 0));
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i += 1) {
    const da = pa[i] ?? 0;
    const db = pb[i] ?? 0;
    if (da !== db) return db - da;
  }
  return 0;
}

/** When the API spans multiple game versions, keep the newest row per catalog identity. */
export function dedupeLootSuggestKeepLatestGameVersion(rows) {
  if (!Array.isArray(rows) || rows.length < 2) return rows;
  const bestByKey = new Map();
  for (let i = 0; i < rows.length; i += 1) {
    const r = rows[i];
    const key = `${r?.tableType ?? ""}::${r?.itemCode ?? ""}`;
    const prev = bestByKey.get(key);
    if (!prev || compareGameVersionPartsDesc(prev.gameVersion, r.gameVersion) > 0) {
      bestByKey.set(key, r);
    }
  }
  if (bestByKey.size === rows.length) return rows;
  const seen = new Set();
  const out = [];
  for (let i = 0; i < rows.length; i += 1) {
    const r = rows[i];
    const key = `${r?.tableType ?? ""}::${r?.itemCode ?? ""}`;
    const best = bestByKey.get(key);
    if (!best || best !== r) continue;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out.length > 0 ? out : rows;
}

function lootSuggestRowKeepRank(row) {
  let rank = 0;
  const pl = row?.payload;
  if (pl && typeof pl === "object" && typeof pl.materialFamilyKey === "string" && pl.materialFamilyKey.trim() !== "") {
    rank -= 10;
  }
  const code = row?.itemCode != null ? String(row.itemCode).toLowerCase() : "";
  if (code.includes("_scu_") || code.includes("_fl_1scu_")) rank -= 5;
  if (code.includes("commodity_")) rank -= 2;
  rank += code.length * 1e-6;
  return rank;
}

function lootSuggestCatalogDedupKey(row) {
  const tt = row?.tableType != null ? String(row.tableType) : "";
  const gv = row?.gameVersion != null ? String(row.gameVersion) : "";
  const mat = lootMaterialDedupKeyForRow(row);
  if (mat) return `${tt}::${gv}::mat:${mat}`;
  const primary = formatLootPrimaryLabel(row).trim().toLowerCase();
  return `${tt}::${gv}::pri:${primary}`;
}

/**
 * Same tableType can expose multiple itemCode rows with identical dropdown labels (duplicate catalog lines).
 */
export function dedupeLootSuggestSamePrimaryDisplayLabel(rows) {
  if (!Array.isArray(rows) || rows.length < 2) return rows;
  const bestByKey = new Map();
  for (let i = 0; i < rows.length; i += 1) {
    const r = rows[i];
    const key = lootSuggestCatalogDedupKey(r);
    const prev = bestByKey.get(key);
    if (!prev || lootSuggestRowKeepRank(r) < lootSuggestRowKeepRank(prev)) {
      bestByKey.set(key, r);
    }
  }
  if (bestByKey.size === rows.length) return rows;
  const seen = new Set();
  const out = [];
  for (let i = 0; i < rows.length; i += 1) {
    const r = rows[i];
    const key = lootSuggestCatalogDedupKey(r);
    const best = bestByKey.get(key);
    if (!best || best !== r || seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out.length > 0 ? out : rows;
}

/**
 * Same display label on two Armor_* tables (e.g. "… Helmet …" under Armor_Arms + Armor_Helmet).
 * Keep the row whose tableType matches the slot hinted by label/itemCode.
 */
function armorSlotTableFromText(text) {
  const t = String(text || "").toLowerCase();
  if (!t) return "";
  if (t.includes("undersuit") || t.includes("flight_suit") || t.includes("flightsuit")) {
    return "Char_Armor_Undersuit";
  }
  if (t.includes("helmet") || t.includes("/helmet/") || t.includes("_helmet_")) {
    return "Armor_Helmet";
  }
  if (t.includes("backpack") || t.includes("_backpack_")) return "Armor_Backpacks";
  if (t.includes("torso") || t.includes("_core_") || t.includes(" core") || t.includes("/core/")) {
    return "Armor_Torso";
  }
  if (t.includes("arms") || t.includes("_arms_") || t.includes("/arms/")) return "Armor_Arms";
  if (t.includes("legs") || t.includes("_legs_") || t.includes("/legs/")) return "Armor_Legs";
  return "";
}

const ARMOR_TABLE_TYPES = new Set([
  "Armor_Helmet",
  "Armor_Torso",
  "Armor_Arms",
  "Armor_Legs",
  "Armor_Backpacks",
  "Char_Armor_Undersuit",
]);

export function dedupeLootSuggestArmorCrossTableSlotMismatch(rows) {
  if (!Array.isArray(rows) || rows.length < 2) return rows;

  const byLabelGv = new Map();
  for (let i = 0; i < rows.length; i += 1) {
    const r = rows[i];
    const tt = String(r?.tableType || "");
    if (!ARMOR_TABLE_TYPES.has(tt)) continue;
    const label = formatLootPrimaryLabel(r).trim().toLowerCase();
    if (!label) continue;
    const gv = r?.gameVersion != null ? String(r.gameVersion) : "";
    const key = `${gv}::${label}`;
    if (!byLabelGv.has(key)) byLabelGv.set(key, []);
    byLabelGv.get(key).push(r);
  }

  const drop = new Set();
  for (const group of byLabelGv.values()) {
    if (group.length < 2) continue;
    const tables = new Set(group.map((r) => String(r.tableType || "")));
    if (tables.size < 2) continue;

    for (const r of group) {
      const hint = armorSlotTableFromText(
        `${formatLootPrimaryLabel(r)} ${r?.itemCode || ""} ${r?.payload?.geometryPrimaryPath || ""}`
      );
      if (!hint) continue;
      if (String(r.tableType) !== hint) {
        // Only drop mismatch when the hinted table is also present in the group.
        if (group.some((o) => String(o.tableType) === hint)) {
          drop.add(r);
        }
      }
    }
  }

  if (drop.size === 0) return rows;
  return rows.filter((r) => !drop.has(r));
}

export function dedupeLootSuggestSameLabelVariousOverCarryablesPacks(rows) {
  if (!Array.isArray(rows) || rows.length < 2) return rows;
  const labelKeys = new Set();
  for (let i = 0; i < rows.length; i += 1) {
    const r = rows[i];
    if (String(r.tableType) !== "variousItems") continue;
    const s = r.label != null ? String(r.label).trim().toLowerCase() : "";
    if (s) labelKeys.add(s);
  }
  if (labelKeys.size === 0) return rows;
  return rows.filter((r) => {
    if (String(r.tableType) !== "Carryables") return true;
    const s = r.label != null ? String(r.label).trim().toLowerCase() : "";
    if (!s || !labelKeys.has(s)) return true;
    if (!itemCodeLooksLikePackedCommodityMineralOrMetalBox(r.itemCode)) return true;
    return false;
  });
}
